"use client"

import { Sparkles } from "lucide-react"
import { useContext } from "react"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ChatContext } from "@/lib/ai-chat/chat-context"
import { useAiCredits } from "@/hooks/ai/use-ai-credits"
import { stateCopy } from "@/lib/ai/quota-copy"
import { useTranslation } from "@/lib/i18n"
import { useRouteGate } from "@/components/auth/permission-gate"

/** The AI chat page, the sidebar's "Ask HF AI": its rule is the button's. */
const AI_CHAT_HREF = "/recipients/ai-chat"

/**
 * HeaderAskAi — shadcn button with Sparkles icon + "Ask AI" label.
 * Renders inline (no fixed positioning of its own — that's the parent's job
 * via HeaderControls). Hidden when chat is open or no ChatProvider in scope.
 *
 * ── Why this READS the allowance and never FETCHES it ───────────────────────
 * `components/header-controls.tsx` mounts this on EVERY authenticated page. A
 * plain `useAiCredits()` here would put a 60-second poll of /me/ai-allowance
 * behind the entire product, for a button that spends nothing and only opens a
 * panel. So it passes `{ enabled: false }`: it serves whatever a real AI
 * surface (the chat panel, an editor, the AI Studio) has already put in the
 * TanStack cache, and issues no request of its own. A cold cache reads
 * `unknown`, which says nothing — correct, because "we could not check" is not
 * worth interrupting someone's navigation over.
 *
 * ── Why it is never DISABLED ────────────────────────────────────────────────
 * Opening the panel is not a spend, and the panel is where the banner, the
 * reset date and the server-filtered remedy live. Disabling the only door to
 * that explanation would leave an exhausted tenant with a dead button and no
 * way to find out why — the exact failure the toolkit exists to prevent.
 *
 * ── Why it follows the "Ask HF AI" menu entry ───────────────────────────────
 * It is the same door as the sidebar's "Ask HF AI" (`/recipients/ai-chat`), so
 * it answers to that page's rule (read:AiChat, lib/auth/nav-permissions.ts):
 * a role the chat refuses gets no button, as it gets no menu entry. Browser QA
 * (2026-10-06): the helpdesk role had no "Ask HF AI" but an enabled "Ask AI"
 * whose panel met nothing but 403s. Fail-open like every gate: drawn while the
 * grants load.
 */
export function HeaderAskAiButton() {
  const ctx = useContext(ChatContext)
  const { state } = useAiCredits({ enabled: false })
  const { t } = useTranslation()
  const chatGate = useRouteGate(AI_CHAT_HREF)

  // `healthy` needs no words and `unknown` has none worth saying here; the
  // three that change what the user should expect do.
  const notable = state === "warning" || state === "exhausted" || state === "degraded"

  if (!ctx) return null
  if (ctx.companionMode !== "closed") return null
  if (chatGate.denied) return null

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={t("nav.askAi")}
          aria-keyshortcuts="Meta+k Control+k"
          data-testid="header-ask-ai-button"
          data-ai-state={state}
          onClick={() => ctx.setCompanionMode("drawer")}
          className="gap-2 shadow-sm"
        >
          <Sparkles className="h-4 w-4" />
          {t("nav.askAi")}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[260px]">
        <p>{t("nav.askAi")}</p>
        {notable && (
          <p data-testid="header-ask-ai-credit-state" className="text-xs">
            {stateCopy(state).headline}
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  )
}
